<template>
    <q-layout view="hHh lpR fFf">
        <custom-sidebar>
            <q-img
                src="/Egoon_logo.svg"
                class="mt-2 mb-5"
                ratio="1"
                fit="contain"
                style="height: var(--logo-size); width: calc(2 * var(--logo-size));"
            />

            <q-list>
                <q-item-label
                    header
                    class="text-textm"
                >
                    {{ $t('label.yourOverview') }}
                </q-item-label>

                <q-item
                    v-for="routeEl of businessRoutes"
                    :key="routeEl.routeName"
                    v-ripple
                    clickable
                    :active="route.name === routeEl.routeName || routeEl.activeRoutes?.includes(route.name)"
                    active-class="fw-b"
                    class="sidebar-option"
                    @click="router.push({ name: routeEl.routeName })"
                >
                    <q-item-section side>
                        <q-icon
                            :name="routeEl.icon"
                            :color="route.name === routeEl.routeName || routeEl.activeRoutes?.includes(route.name) ? 'primary' : 'text'"
                        />
                    </q-item-section>

                    <q-item-section>
                        {{ $t(`label.route.${routeEl.routeName}`) }}
                    </q-item-section>

                    <q-item-section
                        v-if="routeEl.marked?.()"
                        side
                    >
                        <q-icon
                            name="circle"
                            class="text-negative"
                        />
                    </q-item-section>
                </q-item>

                <q-item-label
                    header
                    class="pt-5 text-textm"
                >
                    EGOON
                </q-item-label>

                <q-item
                    v-for="supportRoute of supportRoutes.filter((routeEl) => !routeEl.exclusiveFor || (routeEl.exclusiveFor && routeEl.exclusiveFor.includes(userStore.currentUser.userRole)))"
                    :key="supportRoute.routeName"
                    v-ripple
                    clickable
                    :active="route.name === supportRoute.routeName"
                    class="sidebar-option"
                    @click="supportRoute.clickFn ? supportRoute.clickFn() : router.push({ name: supportRoute.routeName })"
                >
                    <q-item-section side>
                        <q-icon
                            :name="supportRoute.icon"
                            color="text"
                        />
                    </q-item-section>

                    <q-item-section>
                        {{ $t(`label.route.${supportRoute.routeName}`) }}
                    </q-item-section>
                </q-item>

                <q-item class="justify-center">
                    <button-base
                        outline
                        text-color="primary"
                        icon="logout"
                        :label="$t('button.logout')"
                        @click="logout()"
                    />
                </q-item>
            </q-list>
        </custom-sidebar>

        <q-page-container>
            <q-page
                padding
                style="padding-top: calc(var(--logo-size, 0px) + 3.75em); min-width: 860px;"
            >
                <slot />
            </q-page>
        </q-page-container>
    </q-layout>
</template>

<script setup lang="ts">
import { QIcon, QImg, QItem, QItemLabel, QItemSection, QLayout, QList, QPage, QPageContainer } from "quasar";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { ButtonBase, CustomSidebar } from "@/components";

import { useBusinessStore } from "@/stores/business.ts";
import { useOrderStore } from "@/stores/order.ts";
import { useUserStore } from "@/stores/user.ts";

import { logout, openStripeCheckout, popToast, request } from "@/functions";

import { FunctionType, StripeProduct, USER_TYPE, USER_TYPE_TYPE } from "@/types";

import router, { route, ROUTE_NAME, ROUTE_NAME_TYPE } from "@/router";

defineProps<{ title?: string }>()

//@ts-expect-error Stripe added as a Package but not defined
// eslint-disable-next-line no-undef
const stripe = Stripe('pk_test_51PvdPNIG5fl8AAo4Z6cblkr4i9sfhXCwuOx9YYMc81TnbQLNr3UW4kMfFsNg9hAkrqbHrZ0H2qzMJihAFnJVw9mc00yt7rNJ99')

const userStore = useUserStore()
const orderStore = useOrderStore()
const businessStore = useBusinessStore()

const { t } = useI18n()

type RouteObject = {
    activeRoutes?: ROUTE_NAME_TYPE[];
    clickFn?: FunctionType
    icon: string;
    marked?: FunctionType<boolean>;
    routeName: ROUTE_NAME_TYPE | string;
}

const businessRoutes = computed<RouteObject[]>(() => [
    {
        activeRoutes: [ROUTE_NAME.DASHBOARD_CONTRACTOR, ROUTE_NAME.DASHBOARD_CUSTOMER],
        icon: 'grid_view',
        routeName: ROUTE_NAME.HOME
    },
    {
        icon: 'format_list_bulleted',
        marked: (): boolean => {
            if (userStore.currentUser?.userRole === USER_TYPE.CUSTOMER) return !!orderStore.userOrders.data.find(order => order.additionalInformation?.unreadRequests > 0)
            return !!orderStore.orders.data.find(order => order.additionalInformation?.unreadRequests > 0)
        },
        // marked: true, //TODO: Make this conditional on new status updates
        routeName: userStore.currentUser?.userRole === USER_TYPE.CUSTOMER ? ROUTE_NAME.ORDERS_AND_REQUESTS : ROUTE_NAME.ORDERS_AND_REQUESTS_CONTRACTOR
    },
    {
        icon: userStore.currentUser?.userRole === USER_TYPE.CUSTOMER ? 'person' : 'handyman',
        routeName: userStore.currentUser?.userRole === USER_TYPE.CUSTOMER ? ROUTE_NAME.EDIT_PROFILE : ROUTE_NAME.EDIT_BUSINESS
    }
])

const fetchClientSecret = async (priceIds: string[]) => {
    const response = await request<{ clientSecret: string; sessionId: string; }>({
        body: {
            priceIds
        },
        method: 'POST',
        route: 'payments/checkoutSession'
    })

    return response?.clientSecret
}

const supportRoutes: ({ exclusiveFor?: USER_TYPE_TYPE[] } & RouteObject)[] = [
    {
        clickFn: async () => {
            if (!userStore.currentBusiness?.isActive) {
                router.push({ name: ROUTE_NAME.CHOOSE_ABO })

                return
            }

            const response = await userStore.getPortalSession()

            if (!response) return

            window.open(response, '_blank')
        },
        exclusiveFor: [USER_TYPE.CONTRACTOR],
        icon: 'payments',
        routeName: ROUTE_NAME.MANAGE_SUBSCRIPTION
    },
    {
        clickFn: async () => {
            const products = await businessStore.getProducts()

            const additionalRequests = products?.filter((product: StripeProduct) => product.metadata.isSubscription === 'false')

            if (!additionalRequests || additionalRequests.length === 0) {
                popToast({
                    color: 'negative',
                    message: t('dialog.noProductFound')
                })
                return
            }

            const checkout = await stripe.initEmbeddedCheckout({
                fetchClientSecret: () => fetchClientSecret(additionalRequests.map(product => product.default_price.id)),
                onComplete: () => {
                    useUserStore().me()
                }
            })

            openStripeCheckout(checkout)
        },
        exclusiveFor: [USER_TYPE.CONTRACTOR],
        icon: 'post_add',
        routeName: 'additionalRequests'
    },
    {
        exclusiveFor: [USER_TYPE.CONTRACTOR],
        icon: 'receipt',
        routeName: ROUTE_NAME.REQUESTS_OVERVIEW
    },
    {
        icon: 'help_outline',
        routeName: ROUTE_NAME.SUPPORT
    }
]
</script>

<style>
:root {
    --logo-size: 50px;
}

.sidebar-option {
    font-size: 16px;
}
</style>
